/**
 * 绘制 / 编辑编排：定型完成、工具切换、清空；图层/选中/指针拆至命名 hooks
 * 由原 MapDrawingBoard 组件逻辑原样迁出（零行为变更）
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import useMapDrawingHistory from './useMapDrawingHistory';
import useMapDrawingExport from './useMapDrawingExport';
import useMapDrawingTools from './useMapDrawingTools';
import useMapDrawingLayers from './useMapDrawingLayers';
import useMapDrawingSelection from './useMapDrawingSelection';
import useMapDrawingPointer from './useMapDrawingPointer';
import { whenMapStyleSettled } from '../utils/dashboard/earthDeepSpace';
import { setOrthoGlbOverlaysSuppressed } from '../utils/orthoGlbMapLayer';
import { TOOLS } from '../utils/mapDrawingBoard/toolDefs';
import {
  pathLengthMeters,
  polygonAreaMeters,
  formatDistance,
  dedupeConsecutiveCoords,
  formatArea,
  openRingCoords,
  uid,
  isAreaFeatureType,
  isLineFeatureType,
  isEditableFeatureType,
  updateBoxSelectOverlay
} from '../utils/mapDrawingBoard';

export default function useMapDrawingEdit({
  getMap,
  mapReady,
  members = [],
  allowPush = true,
  allowExport = true,
  onActiveChange,
  onStartInteract,
  onTrackPushed
}) {

  const [tool, setTool] = useState(null);
  const [features, setFeatures] = useState([]);
  const [selectedId, setSelectedIdState] = useState(null);
  /** 多选 id 列表（框选）；selectedId 为其中第一项，兼容单选逻辑 */
  const [selectedIds, setSelectedIdsState] = useState([]);
  const [draftPoints, setDraftPoints] = useState([]);
  const [statusText, setStatusText] = useState('');
  const [ctxMenu, setCtxMenu] = useState(null);
  /** 是否正在编辑已定型路线/测距（驱动侧栏按钮文案） */
  const [isEditingPath, setIsEditingPath] = useState(false);

  const {
    expanded, setExpanded,
    pinned, setPinned,
    handleMouseEnter, handleMouseLeave,
    drawColor, setDrawColor,
    drawOpacity, setDrawOpacity,
    sketchLineWidth, setSketchLineWidth,
    sketchPreviewColor, setSketchPreviewColor,
    textStyle, setTextStyle,
    textContent, setTextContent,
    textEditOpen, setTextEditOpen,
    colorRef, opacityRef,
    sketchWidthRef, sketchPreviewColorRef,
    textStyleRef, textContentRef
  } = useMapDrawingTools({ getMap, onActiveChange, tool });

  const draftRef = useRef([]);
  const toolRef = useRef(null);
  const featuresRef = useRef([]);
  const handlersRef = useRef({});
  /** 测距距离标注（贴在线段上） */
  const measureMarkersRef = useRef([]);
  /** 正在编辑的已定型图形 id（从列表取出改路径） */
  const editingIdRef = useRef(null);
  const editingMetaRef = useRef(null);
  /** 顶点拖拽：{ kind:'draft'|'feature', featureId?, index, moved } */
  const dragRef = useRef(null);
  /** 拖拽结束后抑制一次 click，避免误加点 */
  const suppressClickRef = useRef(false);
  const selectedIdRef = useRef(null);
  const selectedIdsRef = useRef([]);
  /** 自由绘画进行中 */
  const sketchStrokeRef = useRef(null);
  /** 框选进行中 */
  const boxSelectRef = useRef(null);
  /** 文字 HTML Marker：id -> marker */
  const textMarkersRef = useRef(new Map());
  const setSelectedIds = useCallback((ids) => {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    selectedIdsRef.current = list;
    selectedIdRef.current = list[0] || null;
    setSelectedIdsState(list);
    setSelectedIdState(list[0] || null);
  }, []);

  const setSelectedId = useCallback((id) => {
    setSelectedIds(id ? [id] : []);
  }, [setSelectedIds]);

  const {
    pushOpen,
    setPushOpen,
    pushFeature,
    pushForm,
    setPushForm,
    pushing,
    memberMeta,
    filteredGroups,
    filteredUsers,
    openPush,
    doExport,
    submitPush
  } = useMapDrawingExport({
    members,
    allowPush,
    onTrackPushed,
    setCtxMenu
  });

  useEffect(() => { toolRef.current = tool; }, [tool]);
  // 画板选中工具时暂时隐藏贴地 3D 全屏画布，避免挡住地图点击
  useEffect(() => {
    const map = getMap?.();
    if (!map) return undefined;
    setOrthoGlbOverlaysSuppressed(map, !!tool);
    return () => setOrthoGlbOverlaysSuppressed(map, false);
  }, [tool, getMap, mapReady]);
  useEffect(() => { featuresRef.current = features; }, [features]);
  useEffect(() => { draftRef.current = draftPoints; }, [draftPoints]);
  // selectedId / selectedIds 由 setSelectedIds 同步写入 ref

  const {
    ensureLayers,
    clearMeasureMarkers,
    clearTextMarkers,
    syncFeaturesToMap,
    syncPreview,
    clearPreview
  } = useMapDrawingLayers({
    getMap,
    onStartInteract,
    measureMarkersRef,
    textMarkersRef,
    featuresRef,
    draftRef,
    toolRef,
    selectedIdRef,
    selectedIdsRef,
    suppressClickRef,
    sketchWidthRef,
    sketchPreviewColorRef,
    setSelectedId,
    setExpanded,
    setPinned,
    setDrawColor,
    setDrawOpacity,
    setTextContent,
    setTextStyle,
    setStatusText,
    setTextEditOpen,
    setFeatures,
    setDraftPoints
  });

  useMapDrawingHistory({
    tool,
    pushOpen,
    getMap,
    syncPreview,
    clearPreview,
    toolRef,
    draftRef,
    setDraftPoints,
    setStatusText
  });

  const finishDraft = useCallback(() => {
    const map = getMap?.();
    const points = draftRef.current;
    const currentTool = toolRef.current;
    if (!map || !currentTool) return;

    if (currentTool === 'route' || currentTool === 'measure') {
      const cleanPoints = dedupeConsecutiveCoords(points, 0.5);
      if (cleanPoints.length < 2) {
        message.warning('至少需要 2 个点');
        return;
      }
      const dist = pathLengthMeters(cleanPoints);
      const editingId = editingIdRef.current;
      const meta = editingMetaRef.current;
      let feature;
      let next;
      if (editingId && meta && (meta.type === 'route' || meta.type === 'measure')) {
        feature = {
          id: editingId,
          type: meta.type,
          coordinates: cleanPoints.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: false,
          name: meta.type === 'measure' ? `测距 ${formatDistance(dist)}` : (meta.name || `路线`),
          meta: { distance: dist }
        };
        next = featuresRef.current.some((f) => f.id === editingId)
          ? featuresRef.current.map((f) => (f.id === editingId ? feature : f))
          : [...featuresRef.current, feature];
        editingIdRef.current = null;
        editingMetaRef.current = null;
        setIsEditingPath(false);
        message.success(`路径已更新（${formatDistance(dist)}）`);
        setStatusText(`已保存修改，全长 ${formatDistance(dist)}`);
      } else {
        feature = {
          id: uid(currentTool === 'route' ? 'route' : 'meas'),
          type: currentTool === 'route' ? 'route' : 'measure',
          coordinates: cleanPoints.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: false,
          name: currentTool === 'route' ? `路线 ${featuresRef.current.filter((f) => f.type === 'route').length + 1}` : `测距 ${formatDistance(dist)}`,
          meta: { distance: dist }
        };
        next = [...featuresRef.current, feature];
        setStatusText(currentTool === 'route'
          ? `已完成路线，全长 ${formatDistance(dist)}。点选后可编辑路径 / Del 删除`
          : `测距完成：${formatDistance(dist)}`);
        message.success(currentTool === 'route' ? `路线已完成（${formatDistance(dist)}）` : `距离 ${formatDistance(dist)}`);
      }
      setFeatures(next);
      featuresRef.current = next;
      syncFeaturesToMap(map, next);
      setSelectedId(feature.id);
    } else if (currentTool === 'polygon') {
      const ringPts = openRingCoords(points);
      if (ringPts.length < 3) {
        message.warning('多边形至少需要 3 个点');
        return;
      }
      const area = polygonAreaMeters(ringPts);
      const editingId = editingIdRef.current;
      const meta = editingMetaRef.current;
      let feature;
      let next;
      if (editingId && meta && isAreaFeatureType(meta.type)) {
        feature = {
          id: editingId,
          type: meta.type,
          coordinates: ringPts.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: meta.fill !== false,
          name: meta.name || (meta.type === 'rectangle' ? '选框' : '区域'),
          meta: { area }
        };
        next = featuresRef.current.some((f) => f.id === editingId)
          ? featuresRef.current.map((f) => (f.id === editingId ? feature : f))
          : [...featuresRef.current, feature];
        editingIdRef.current = null;
        editingMetaRef.current = null;
        setIsEditingPath(false);
        message.success(`形状已更新（${formatArea(area)}）`);
        setStatusText(`已保存修改，面积 ${formatArea(area)}`);
      } else {
        feature = {
          id: uid('poly'),
          type: 'polygon',
          coordinates: ringPts.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: true,
          name: `区域 ${featuresRef.current.filter((f) => f.type === 'polygon').length + 1}`,
          meta: { area }
        };
        next = [...featuresRef.current, feature];
        setStatusText(`区域面积 ${formatArea(area)}。点选后可编辑形状 / Del 删除`);
        message.success(`区域已完成（${formatArea(area)}）`);
      }
      setFeatures(next);
      featuresRef.current = next;
      syncFeaturesToMap(map, next);
      setSelectedId(feature.id);
    } else if (currentTool === 'rectangle') {
      // 编辑中的矩形走 polygon 工具；此处仅处理「对角两点」新建
      if (editingIdRef.current && editingMetaRef.current && isAreaFeatureType(editingMetaRef.current.type)) {
        // 若误用 rectangle 工具保存，按多边形逻辑用当前草稿点
        const ringPts = openRingCoords(points);
        if (ringPts.length < 3) {
          message.warning('至少需要 3 个点');
          return;
        }
        const area = polygonAreaMeters(ringPts);
        const editingId = editingIdRef.current;
        const meta = editingMetaRef.current;
        const feature = {
          id: editingId,
          type: meta.type,
          coordinates: ringPts.map((p) => [...p]),
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: meta.fill !== false,
          name: meta.name || '选框',
          meta: { area }
        };
        const next = featuresRef.current.some((f) => f.id === editingId)
          ? featuresRef.current.map((f) => (f.id === editingId ? feature : f))
          : [...featuresRef.current, feature];
        editingIdRef.current = null;
        editingMetaRef.current = null;
        setIsEditingPath(false);
        setFeatures(next);
        featuresRef.current = next;
        syncFeaturesToMap(map, next);
        setSelectedId(feature.id);
        message.success(`形状已更新（${formatArea(area)}）`);
        setStatusText(`已保存修改，面积 ${formatArea(area)}`);
      } else if (points.length < 2) {
        message.warning('请再点对角完成矩形');
        return;
      } else {
        const [a, b] = points;
        const ring = [
          [a[0], a[1]],
          [b[0], a[1]],
          [b[0], b[1]],
          [a[0], b[1]]
        ];
        const area = polygonAreaMeters(ring);
        const feature = {
          id: uid('rect'),
          type: 'rectangle',
          coordinates: ring,
          color: colorRef.current,
          opacity: opacityRef.current,
          fill: true,
          name: `选框 ${featuresRef.current.filter((f) => f.type === 'rectangle').length + 1}`,
          meta: { area }
        };
        const next = [...featuresRef.current, feature];
        setFeatures(next);
        featuresRef.current = next;
        syncFeaturesToMap(map, next);
        setSelectedId(feature.id);
        setStatusText(`矩形面积 ${formatArea(area)}。点选后可编辑形状 / Del 删除`);
        message.success(`矩形已完成（${formatArea(area)}）`);
      }
    }

    clearPreview();
  }, [getMap, syncFeaturesToMap, clearPreview]);

  const {
    patchSelectedFeature,
    deleteSelectedFeature,
    startEditFeature,
    cancelCurrentDrawing
  } = useMapDrawingSelection({
    getMap,
    onStartInteract,
    features,
    setFeatures,
    featuresRef,
    draftRef,
    toolRef,
    editingIdRef,
    editingMetaRef,
    selectedId,
    selectedIds,
    selectedIdRef,
    selectedIdsRef,
    setSelectedId,
    setSelectedIds,
    syncFeaturesToMap,
    syncPreview,
    clearPreview,
    setDraftPoints,
    setTool,
    setDrawColor,
    setDrawOpacity,
    setSketchLineWidth,
    setTextContent,
    setTextStyle,
    setTextEditOpen,
    setExpanded,
    setPinned,
    setCtxMenu,
    setStatusText,
    setIsEditingPath,
    drawColor,
    drawOpacity,
    sketchLineWidth
  });

  useMapDrawingPointer({
    mapReady,
    getMap,
    onStartInteract,
    pushOpen,
    ensureLayers,
    syncFeaturesToMap,
    syncPreview,
    clearPreview,
    clearMeasureMarkers,
    clearTextMarkers,
    finishDraft,
    deleteSelectedFeature,
    setSelectedId,
    setSelectedIds,
    handlersRef,
    featuresRef,
    draftRef,
    toolRef,
    dragRef,
    suppressClickRef,
    selectedIdRef,
    selectedIdsRef,
    sketchStrokeRef,
    boxSelectRef,
    editingIdRef,
    colorRef,
    opacityRef,
    sketchWidthRef,
    textStyleRef,
    textContentRef,
    setDraftPoints,
    setFeatures,
    setDrawColor,
    setDrawOpacity,
    setSketchLineWidth,
    setTextContent,
    setTextEditOpen,
    setExpanded,
    setPinned,
    setStatusText,
    setCtxMenu
  });

  const selectTool = (key) => {
    onStartInteract?.();
    setCtxMenu(null);
    // 切换工具前若在编辑已定型线，先还原
    if (editingIdRef.current) {
      cancelCurrentDrawing();
    } else {
      clearPreview();
    }
    if (tool === key) {
      setTool(null);
      setStatusText('');
      return;
    }
    setTool(key);
    const tip = TOOLS.find((t) => t.key === key)?.tip || '';
    setStatusText(tip);
    // 切底图后图层可能被清掉：选工具时立刻尝试重建
    const map = getMap?.();
    if (map) {
      if (!ensureLayers(map)) {
        whenMapStyleSettled(map, () => {
          ensureLayers(map);
          syncFeaturesToMap(map, featuresRef.current);
        }, 1500);
      } else {
        syncFeaturesToMap(map, featuresRef.current);
      }
    }
  };

  const clearAll = () => {
    Modal.confirm({
      title: '清空画板？',
      content: '将删除当前地图上所有绘制图形（不影响救援推送轨迹）',
      okText: '清空',
      cancelText: '取消',
      onOk: () => {
        const map = getMap?.();
        editingIdRef.current = null;
        editingMetaRef.current = null;
        setIsEditingPath(false);
        setFeatures([]);
        featuresRef.current = [];
        setSelectedIds([]);
        clearPreview();
        clearTextMarkers();
        updateBoxSelectOverlay(map, null);
        if (map) syncFeaturesToMap(map, []);
        message.success('已清空画板');
      }
    });
  };

  const selectedFeature = features.find((f) => f.id === selectedId) || null;
  const ctxFeature = ctxMenu
    ? features.find((f) => f.id === ctxMenu.featureId)
    : null;

  return {
    // panel
    expanded, setExpanded, pinned, setPinned,
    handleMouseEnter, handleMouseLeave,
    // tools / palette
    tool, selectTool,
    drawColor, setDrawColor,
    drawOpacity, setDrawOpacity,
    sketchLineWidth, setSketchLineWidth,
    sketchPreviewColor, setSketchPreviewColor,
    textStyle, setTextStyle,
    textContent, setTextContent,
    textEditOpen, setTextEditOpen,
    statusText, isEditingPath,
    // features
    features, setFeatures, featuresRef,
    selectedId, selectedIds, setSelectedId, setSelectedIds, selectedIdsRef,
    selectedFeature, ctxMenu, setCtxMenu, ctxFeature,
    draftPoints,
    // actions
    finishDraft, cancelCurrentDrawing, clearAll,
    startEditFeature, deleteSelectedFeature, patchSelectedFeature,
    // export / push
    allowPush, allowExport,
    openPush, doExport, submitPush,
    pushOpen, setPushOpen, pushFeature, pushForm, setPushForm, pushing,
    memberMeta, filteredGroups, filteredUsers,
    // map helpers used by list delete
    getMap, syncFeaturesToMap,
    // formatters for UI
    formatDistance, formatArea,
    isLineFeatureType, isAreaFeatureType, isEditableFeatureType
  };
}
